import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveTour, setTourCompleted, resetTour } from '../redux/tour/action';

/**
 * Custom hook for managing product tours in the application
 * 
 * @param tourId - Unique identifier for the tour
 * @param autoStart - Whether to automatically start the tour if it hasn't been completed
 * @param dependencies - Array of dependencies that should trigger tour evaluation (e.g., loading state, data availability)
 * @returns Tour management functions and state
 */
const useTourManager = (
  tourId: string,
  autoStart: boolean = false,
  dependencies: any[] = []
) => {
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const dispatch = useDispatch();
  const { completedTours, activeTour } = useSelector((state: any) => state.tour);
  
  // Use refs to prevent excessive re-evaluations
  const hasAutoStartedRef = useRef(false);
  const lastDependenciesRef = useRef<any[]>([]);
  const dependencyCheckCountRef = useRef(0);
  
  // Check if this tour has been completed before - memoized for stability
  const isTourCompleted = useMemo(() => completedTours?.[tourId], [completedTours, tourId]);
  const isFirstVisit = useMemo(() => !isTourCompleted, [isTourCompleted]);
  
  // Memoized stable dependency checker
  const areDependenciesMet = useMemo(() => {
    // If no dependencies provided, consider them met
    if (dependencies.length === 0) return true;
    
    // Check if dependencies have changed
    const dependenciesChanged = JSON.stringify(dependencies) !== JSON.stringify(lastDependenciesRef.current);
    if (dependenciesChanged) {
      lastDependenciesRef.current = [...dependencies];
    }
    
    // Dependencies are met if none of them are truthy loading states, null, or undefined
    return !dependencies.includes(true) && !dependencies.includes(null) && !dependencies.includes(undefined);
  }, [dependencies]);
  
  // Stable start tour function
  const startTour = useCallback(() => {
    dispatch(setActiveTour(tourId));
    setShowTour(true);
    setCurrentStep(0);
  }, [dispatch, tourId]);
  
  // Stable complete tour function
  const completeTour = useCallback(() => {
    dispatch(setTourCompleted(tourId));
    dispatch(setActiveTour(null));
    setShowTour(false);
    setCurrentStep(0);
    hasAutoStartedRef.current = false; // Reset for potential future use
  }, [dispatch, tourId]);
  
  // Stable skip tour function
  const skipTour = useCallback(() => {
    dispatch(setTourCompleted(tourId));
    dispatch(setActiveTour(null));
    setShowTour(false);
    setCurrentStep(0);
    hasAutoStartedRef.current = false; // Reset for potential future use
  }, [dispatch, tourId]);
  
  // Stable restart tour function
  const restartTour = useCallback(() => {
    dispatch(resetTour(tourId));
    dispatch(setActiveTour(tourId));
    setShowTour(true);
    setCurrentStep(0);
    hasAutoStartedRef.current = true; // Mark as started to prevent auto-start conflicts
  }, [dispatch, tourId]);

  // Stable update step function
  const updateStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);
  
  // DISABLED: Auto-start logic removed to prevent unwanted tour activation
  // Tours should only start when explicitly triggered by user interaction (e.g., clicking "Start" button)
  useEffect(() => {
    // Increment dependency check counter for debugging
    dependencyCheckCountRef.current++;
    
    // Prevent excessive dependency checks that could cause infinite loops
    if (dependencyCheckCountRef.current > 10) {
      return;
    }
    
    // AUTO-START DISABLED: Tours will only start when explicitly called
    // This ensures tours only start when user clicks "Start" button in modal
    // Previous auto-start logic removed to prevent unwanted tour activation
    
    // Reset hasAutoStartedRef when component unmounts or conditions change
    // to allow manual tour starts to work properly
    if (!showTour && hasAutoStartedRef.current) {
      hasAutoStartedRef.current = false;
    }
  }, [showTour]); // Only depend on showTour to reset the ref when needed
  
  // Return memoized object to prevent unnecessary re-renders
  return useMemo(() => ({
    showTour,
    isFirstVisit,
    isTourCompleted,
    currentStep,
    startTour,
    completeTour,
    skipTour,
    restartTour,
    updateStep
  }), [
    showTour,
    isFirstVisit,
    isTourCompleted,
    currentStep,
    startTour,
    completeTour,
    skipTour,
    restartTour,
    updateStep
  ]);
};

export default useTourManager; 